import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * Calendar Events (SAN001P-93)
 *
 * Conveyor-belt carousel: cards + date tabs share one logical index.
 * Loop is done by cloning the track (3 copies) and teleporting back to the
 * middle copy after a wrap — no Swiper.
 *
 * Markup:
 *   [data-ce-tabs] > [data-ce-tabs-track] > [data-ce-tab][data-ce-index]
 *   [data-ce-viewport] > [data-ce-cards-track] > [data-ce-slide][data-ce-index]
 *   [data-ce-prev] / [data-ce-next]
 *   [data-ce-open][data-ce-index][data-ce-tab]
 *   [data-ce-event][data-ce-index]
 *
 * Host: data-initial-index, data-klaviyo-company-id, notify message attrs
 */

/**
 * One looping strip: clones items, centers on an index, teleports after wrap.
 */
class ConveyorStrip {
  /** @param {{ viewport: HTMLElement, track: HTMLElement, itemSelector: string, loop: boolean }} opts */
  constructor({ viewport, track, itemSelector, loop }) {
    this.viewport = viewport;
    this.track = track;
    this.itemSelector = itemSelector;
    this.loop = loop;
    /** @type {number} */
    this.count = 0;
    /** @type {number} physical index into the (possibly cloned) track */
    this.physical = 0;
    /** @type {boolean} */
    this.animating = false;
    /** @type {((logical: number) => void) | null} */
    this.onSettle = null;

    this.#build();
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return /** @type {HTMLElement[]} */ ([...this.track.querySelectorAll(this.itemSelector)]);
  }

  get logical() {
    if (this.count <= 0) return 0;
    return ((this.physical % this.count) + this.count) % this.count;
  }

  #build() {
    const originals = [...this.track.children].filter((el) => el instanceof HTMLElement);
    this.count = originals.length;
    if (this.count === 0) return;

    if (this.loop && this.count >= 2) {
      const head = originals.map((el) => this.#clone(el));
      const tail = originals.map((el) => this.#clone(el));
      head.reverse().forEach((el) => this.track.insertBefore(el, this.track.firstChild));
      tail.forEach((el) => this.track.appendChild(el));
      // Middle copy starts at index `count`
      this.physical = this.count;
    } else {
      this.physical = 0;
    }

    this.track.addEventListener('transitionend', this.#onTransitionEnd);
  }

  /** @param {HTMLElement} el */
  #clone(el) {
    const node = /** @type {HTMLElement} */ (el.cloneNode(true));
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('data-ce-clone', 'true');
    if (node.hasAttribute('aria-current')) node.setAttribute('aria-current', 'false');
    if (node.hasAttribute('aria-selected')) node.setAttribute('aria-selected', 'false');
    node.classList.remove('is-active');
    node.tabIndex = -1;
    return node;
  }

  destroy() {
    this.track.removeEventListener('transitionend', this.#onTransitionEnd);
    this.track.querySelectorAll('[data-ce-clone]').forEach((el) => el.remove());
    this.track.style.transform = '';
    this.track.style.transition = '';
  }

  /**
   * @param {number} logical
   * @param {{ animate?: boolean, preferPhysical?: number }} [opts]
   */
  goTo(logical, { animate = true, preferPhysical } = {}) {
    if (this.count <= 0) return;
    const target = ((logical % this.count) + this.count) % this.count;

    let physical = this.count > 0 ? this.count + target : target; // middle copy default
    if (this.loop && this.count >= 2) {
      const candidates = [target, this.count + target, this.count * 2 + target];
      if (Number.isFinite(preferPhysical)) {
        const preferred = candidates.reduce((best, c) =>
          Math.abs(c - /** @type {number} */ (preferPhysical)) < Math.abs(best - /** @type {number} */ (preferPhysical))
            ? c
            : best
        );
        physical = preferred;
      } else {
        physical = candidates.reduce((best, c) =>
          Math.abs(c - this.physical) < Math.abs(best - this.physical) ? c : best
        );
      }
    } else {
      physical = target;
    }

    this.#moveTo(physical, animate);
  }

  /**
   * Step by ±1 along the conveyor (true belt motion).
   * @param {1 | -1} dir
   * @param {boolean} [animate]
   */
  step(dir, animate = true) {
    if (this.count <= 0) return;
    if (this.loop && this.count >= 2) {
      this.#moveTo(this.physical + dir, animate);
    } else {
      const next = Math.max(0, Math.min(this.count - 1, this.logical + dir));
      this.#moveTo(next, animate);
    }
  }

  /**
   * Jump to a specific DOM item (e.g. the tab the user clicked).
   * @param {HTMLElement} el
   * @param {boolean} [animate]
   */
  goToElement(el, animate = true) {
    const idx = this.items.indexOf(el);
    if (idx < 0) return;
    this.#moveTo(idx, animate);
  }

  /** Re-center current physical index after resize (no animation). */
  refresh() {
    this.#moveTo(this.physical, false);
  }

  /**
   * Snap to whichever item center is closest to the viewport center.
   * @param {boolean} [animate]
   */
  snapNearest(animate = true) {
    const items = this.items;
    if (!items.length) return;
    const vp = this.viewport.getBoundingClientRect();
    const center = (vp.left + vp.right) / 2;
    let best = this.physical;
    let bestDist = Infinity;
    items.forEach((item, i) => {
      const r = item.getBoundingClientRect();
      const dist = Math.abs((r.left + r.right) / 2 - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    this.#moveTo(best, animate);
  }

  /**
   * @param {number} physical
   * @param {boolean} animate
   */
  #moveTo(physical, animate) {
    const items = this.items;
    if (!items.length) return;
    const max = items.length - 1;
    physical = Math.max(0, Math.min(max, physical));
    this.physical = physical;
    this.animating = animate;

    const item = items[physical];
    if (!item) return;

    // Center item in viewport.
    const x = this.viewport.clientWidth / 2 - (item.offsetLeft + item.offsetWidth / 2);
    this.track.style.transition = animate ? 'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;

    if (!animate) {
      // Force reflow so a following animated move still transitions.
      void this.track.offsetWidth;
      this.animating = false;
      this.#normalize();
      this.onSettle?.(this.logical);
    }
  }

  #onTransitionEnd = (event) => {
    if (event.target !== this.track) return;
    if (event.propertyName && event.propertyName !== 'transform') return;
    this.animating = false;
    this.#normalize();
    this.onSettle?.(this.logical);
  };

  /** If we landed on a clone set, teleport to the equivalent middle-copy slide. */
  #normalize() {
    if (!this.loop || this.count < 2) return;
    const n = this.count;
    if (this.physical >= n && this.physical < n * 2) return;

    const logical = this.logical;
    const middle = n + logical;
    if (middle === this.physical) return;

    this.physical = middle;
    const item = this.items[middle];
    if (!item) return;
    const x = this.viewport.clientWidth / 2 - (item.offsetLeft + item.offsetWidth / 2);
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
    void this.track.offsetWidth;
  }
}

class CalendarEventsComponent extends DialogComponent {
  /** @type {string[]} */
  requiredRefs = ['dialog'];

  /** @type {ConveyorStrip | null} */
  #cards = null;

  /** @type {ConveyorStrip | null} */
  #tabs = null;

  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #slideCount = 0;

  /** @type {boolean} */
  #loop = false;

  /** @type {boolean} */
  #ready = false;

  /** @type {boolean} */
  #syncing = false;

  /** @type {number} */
  #resizeTicking = 0;

  /** @type {HTMLElement | null} */
  #restoreFocusEl = null;

  // Drag state (cards only)
  /** @type {boolean} */
  #dragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragOriginTranslate = 0;

  /** @type {boolean} */
  #dragMoved = false;

  connectedCallback() {
    super.connectedCallback();
    this.#logEvents();

    const initial = Number(this.dataset.initialIndex || 0);
    this.#activeIndex = Number.isFinite(initial) ? initial : 0;
    this.#slideCount = this.querySelectorAll('[data-ce-cards-track] [data-ce-slide]').length;
    this.#loop = this.#slideCount >= 2;

    this.addEventListener('click', this.#onDelegatedClick);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClosed);
    window.addEventListener('resize', this.#onWindowResize);

    this.querySelectorAll('[data-ce-dialog-tab]').forEach((tab) => {
      tab.addEventListener('click', this.#onDialogTabClick);
    });
    this.querySelectorAll('[data-ce-notify-form]').forEach((form) => {
      form.addEventListener('submit', this.#onNotifySubmit);
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#initConveyors());
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onDelegatedClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClosed);
    window.removeEventListener('resize', this.#onWindowResize);
    this.#teardownDrag();
    this.#cards?.destroy();
    this.#tabs?.destroy();
    this.#cards = null;
    this.#tabs = null;
  }

  #initConveyors() {
    if (!this.isConnected) return;

    const cardsViewport = this.querySelector('[data-ce-viewport]');
    const cardsTrack = this.querySelector('[data-ce-cards-track]');
    const tabsViewport = this.querySelector('[data-ce-tabs]');
    const tabsTrack = this.querySelector('[data-ce-tabs-track]');

    if (
      !(cardsViewport instanceof HTMLElement) ||
      !(cardsTrack instanceof HTMLElement) ||
      !(tabsViewport instanceof HTMLElement) ||
      !(tabsTrack instanceof HTMLElement)
    ) {
      return;
    }

    this.#cards = new ConveyorStrip({
      viewport: cardsViewport,
      track: cardsTrack,
      itemSelector: '[data-ce-slide]',
      loop: this.#loop,
    });
    this.#tabs = new ConveyorStrip({
      viewport: tabsViewport,
      track: tabsTrack,
      itemSelector: '[data-ce-tab]',
      loop: this.#loop,
    });

    this.#cards.onSettle = (logical) => this.#onStripSettle('cards', logical);
    this.#tabs.onSettle = (logical) => this.#onStripSettle('tabs', logical);

    this.#setupDrag(cardsViewport);

    // Land on Liquid initial index (middle copy), no animation.
    this.#goTo(this.#activeIndex, { animate: false });
    this.#ready = true;

    document.fonts?.ready?.then?.(() => {
      if (!this.isConnected || !this.#ready) return;
      this.#cards?.refresh();
      this.#tabs?.refresh();
      this.#syncUi(this.#activeIndex);
    });
  }

  /**
   * @param {'cards' | 'tabs'} source
   * @param {number} logical
   */
  #onStripSettle(source, logical) {
    if (!this.#ready || this.#syncing) return;
    if (logical === this.#activeIndex) {
      this.#syncUi(logical);
      return;
    }
    this.#activeIndex = logical;
    this.#syncUi(logical);
    this.#syncing = true;
    if (source !== 'cards') this.#cards?.goTo(logical, { animate: true });
    if (source !== 'tabs') this.#tabs?.goTo(logical, { animate: true });
    this.#syncing = false;
  }

  /**
   * @param {number} logical
   * @param {{ animate?: boolean, preferCardsPhysical?: number, preferTabsPhysical?: number }} [opts]
   */
  #goTo(logical, { animate = true, preferCardsPhysical, preferTabsPhysical } = {}) {
    const target = Math.max(0, Math.min(this.#slideCount - 1, logical));
    this.#activeIndex = target;
    this.#syncUi(target);
    this.#syncing = true;
    this.#cards?.goTo(target, { animate, preferPhysical: preferCardsPhysical });
    this.#tabs?.goTo(target, { animate, preferPhysical: preferTabsPhysical });
    this.#syncing = false;
  }

  /** @param {1 | -1} dir */
  #step(dir) {
    if (this.#slideCount <= 0) return;
    if (this.#cards?.animating || this.#tabs?.animating) return;

    const preferCards = (this.#cards?.physical ?? 0) + dir;
    const preferTabs = (this.#tabs?.physical ?? 0) + dir;
    const next = this.#loop
      ? (((this.#activeIndex + dir) % this.#slideCount) + this.#slideCount) % this.#slideCount
      : Math.max(0, Math.min(this.#slideCount - 1, this.#activeIndex + dir));

    this.#goTo(next, {
      animate: true,
      preferCardsPhysical: preferCards,
      preferTabsPhysical: preferTabs,
    });
  }

  #onWindowResize = () => {
    if (this.#resizeTicking) cancelAnimationFrame(this.#resizeTicking);
    this.#resizeTicking = requestAnimationFrame(() => {
      this.#resizeTicking = 0;
      if (!this.#ready) return;
      this.#cards?.refresh();
      this.#tabs?.refresh();
      this.#syncUi(this.#activeIndex);
    });
  };

  #onDelegatedClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-ce-open]');
    if (openBtn instanceof HTMLElement && this.contains(openBtn)) {
      const index = Number(openBtn.dataset.ceIndex);
      const tab = openBtn.dataset.ceTab === 'notify' ? 'notify' : 'info';
      if (Number.isFinite(index)) this.openEvent(index, tab, openBtn);
      return;
    }

    const prev = target.closest('[data-ce-prev]');
    if (prev && this.contains(prev)) {
      event.preventDefault();
      this.#step(-1);
      return;
    }

    const next = target.closest('[data-ce-next]');
    if (next && this.contains(next)) {
      event.preventDefault();
      this.#step(1);
      return;
    }

    const tab = target.closest('[data-ce-tab]');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      const index = Number(tab.dataset.ceIndex);
      if (!Number.isFinite(index)) return;
      tab.focus?.({ preventScroll: true });

      this.#activeIndex = index;
      this.#syncUi(index);
      this.#syncing = true;
      this.#tabs?.goToElement(tab, true);
      this.#cards?.goTo(index, {
        animate: true,
        preferPhysical: this.#cards ? this.#nearestPhysical(this.#cards, index) : undefined,
      });
      this.#syncing = false;
    }
  };

  /**
   * @param {ConveyorStrip} strip
   * @param {number} logical
   */
  #nearestPhysical(strip, logical) {
    if (!strip.loop || strip.count < 2) return logical;
    const n = strip.count;
    const candidates = [logical, n + logical, n * 2 + logical];
    return candidates.reduce((best, c) =>
      Math.abs(c - strip.physical) < Math.abs(best - strip.physical) ? c : best
    );
  }

  // --- Drag (cards) ---------------------------------------------------------

  /** @param {HTMLElement} viewport */
  #setupDrag(viewport) {
    viewport.addEventListener('pointerdown', this.#onPointerDown);
    viewport.addEventListener('pointermove', this.#onPointerMove);
    viewport.addEventListener('pointerup', this.#onPointerUp);
    viewport.addEventListener('pointercancel', this.#onPointerUp);
    viewport.addEventListener('lostpointercapture', this.#onPointerUp);
  }

  #teardownDrag() {
    const viewport = this.querySelector('[data-ce-viewport]');
    if (!(viewport instanceof HTMLElement)) return;
    viewport.removeEventListener('pointerdown', this.#onPointerDown);
    viewport.removeEventListener('pointermove', this.#onPointerMove);
    viewport.removeEventListener('pointerup', this.#onPointerUp);
    viewport.removeEventListener('pointercancel', this.#onPointerUp);
    viewport.removeEventListener('lostpointercapture', this.#onPointerUp);
  }

  #readTranslateX() {
    const track = this.#cards?.track;
    if (!track) return 0;
    const match = /translate3d\(([-\d.]+)px/.exec(track.style.transform || '');
    return match ? parseFloat(match[1]) : 0;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 || !this.#cards) return;
    // Don't steal clicks from buttons/links inside cards.
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragStartX = event.clientX;
    this.#dragOriginTranslate = this.#readTranslateX();
    this.#cards.track.style.transition = 'none';
    event.currentTarget instanceof HTMLElement &&
      event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging || !this.#cards) return;
    const dx = event.clientX - this.#dragStartX;
    if (Math.abs(dx) > 6) this.#dragMoved = true;
    this.#cards.track.style.transform = `translate3d(${this.#dragOriginTranslate + dx}px, 0, 0)`;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging || !this.#cards) return;
    this.#dragging = false;

    if (!this.#dragMoved) {
      this.#cards.refresh();
      return;
    }

    const dx = event.clientX - this.#dragStartX;
    // Flick: step one; otherwise snap nearest.
    if (Math.abs(dx) > 60) {
      this.#step(dx < 0 ? 1 : -1);
    } else {
      this.#cards.snapNearest(true);
      // onSettle will sync tabs
    }
  };

  #logEvents() {
    const el = this.querySelector('[data-ce-events-json]');
    if (!(el instanceof HTMLScriptElement)) {
      console.log('[calendar-events] no events JSON found', { initialIndex: this.dataset.initialIndex });
      return;
    }
    try {
      const events = JSON.parse(el.textContent || '[]');
      console.log('[calendar-events]', {
        count: events.length,
        initialIndex: Number(this.dataset.initialIndex || 0),
        klaviyoCompanyId: this.dataset.klaviyoCompanyId || null,
        events,
      });
    } catch (error) {
      console.warn('[calendar-events] failed to parse events JSON', error);
    }
  }

  /**
   * @param {number} index
   * @param {'info' | 'notify'} tab
   * @param {HTMLElement | null} [trigger]
   */
  openEvent(index, tab = 'info', trigger = null) {
    this.#restoreFocusEl = trigger;
    this.#showEventPanel(index);
    this.#resetNotifyForm(index);
    this.setDialogTab(tab);
    this.showDialog();
  }

  /** @param {string} tab */
  setDialogTab(tab) {
    if (tab !== 'info' && tab !== 'notify') return;
    this.dataset.activeDialogTab = tab;

    const eventPanel = this.querySelector('[data-ce-event].is-active');
    if (!eventPanel) return;

    const notifyTab = eventPanel.querySelector('[data-ce-dialog-tab="notify"]');
    if (tab === 'notify' && !notifyTab) {
      tab = 'info';
      this.dataset.activeDialogTab = 'info';
    }

    eventPanel.querySelectorAll('[data-ce-dialog-tab]').forEach((el) => {
      const isActive = el instanceof HTMLElement && el.dataset.ceDialogTab === tab;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
      el.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    eventPanel.querySelectorAll('[data-ce-dialog-panel]').forEach((panel) => {
      const isActive = panel instanceof HTMLElement && panel.dataset.ceDialogPanel === tab;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  }

  /** @param {number} index */
  #resetNotifyForm(index) {
    const panel = this.querySelector(`[data-ce-event][data-ce-index="${index}"]`);
    if (!(panel instanceof HTMLElement)) return;
    const formEl = panel.querySelector('[data-ce-notify-form]');
    if (!(formEl instanceof HTMLFormElement)) return;
    formEl.reset();
    const controls = formEl.querySelector('[data-ce-notify-controls]');
    if (controls instanceof HTMLElement) controls.hidden = false;
    const message = formEl.querySelector('[data-ce-notify-message]');
    if (message instanceof HTMLElement) {
      message.hidden = true;
      message.textContent = '';
      message.classList.remove(
        'calendar-events__notify-message--error',
        'calendar-events__notify-message--success'
      );
    }
    const submit = formEl.querySelector('[type="submit"]');
    if (submit instanceof HTMLButtonElement || submit instanceof HTMLInputElement) {
      submit.disabled = false;
    }
  }

  /** @param {number} index */
  #syncUi(index) {
    this.querySelectorAll('[data-ce-slide]').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const isMatch = Number(slide.dataset.ceIndex) === index && !slide.hasAttribute('data-ce-clone');
      slide.classList.toggle('is-active', Number(slide.dataset.ceIndex) === index);
      if (!slide.hasAttribute('data-ce-clone')) {
        slide.setAttribute('aria-current', isMatch ? 'true' : 'false');
      }
    });

    const n = this.#slideCount;
    this.querySelectorAll('[data-ce-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const tabIndex = Number(tab.dataset.ceIndex);
      const raw = Math.abs(tabIndex - index);
      const distance = this.#loop && n > 0 ? Math.min(raw, n - raw) : raw;
      const isActive = tabIndex === index;
      tab.classList.toggle('is-active', isActive);
      if (!tab.hasAttribute('data-ce-clone')) {
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      }
      let opacity = 0.28;
      if (distance === 0) opacity = 1;
      else if (distance === 1) opacity = 0.64;
      else if (distance === 2) opacity = 0.5;
      tab.style.setProperty('--ce-tab-opacity', String(opacity));
    });

    const prev = this.querySelector('[data-ce-prev]');
    const next = this.querySelector('[data-ce-next]');
    const hideNav = this.#slideCount <= 1;
    if (prev instanceof HTMLElement) prev.hidden = hideNav;
    if (next instanceof HTMLElement) next.hidden = hideNav;
  }

  /** @param {number} index */
  #showEventPanel(index) {
    this.querySelectorAll('[data-ce-event]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = Number(panel.dataset.ceIndex) === index;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      if (isActive) {
        const bg = panel.style.getPropertyValue('--ce-panel-bg').trim();
        const text = panel.style.getPropertyValue('--ce-panel-text').trim();
        const fg = panel.style.getPropertyValue('--ce-panel-fg').trim();
        if (bg) this.style.setProperty('--ce-panel-bg', bg);
        if (text) this.style.setProperty('--ce-panel-text', text);
        if (fg) this.style.setProperty('--ce-panel-fg', fg);
      }
    });
  }

  #onDialogTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const name = tab.dataset.ceDialogTab;
    if (name) this.setDialogTab(name);
  };

  #onDialogClosed = () => {
    const el = this.#restoreFocusEl;
    this.#restoreFocusEl = null;
    if (el instanceof HTMLElement && document.contains(el)) {
      el.focus({ preventScroll: true });
    }
  };

  #onNotifySubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const message = form.querySelector('[data-ce-notify-message]');
    const submit = form.querySelector('[type="submit"]');
    const emailInput = form.querySelector('[name="email"]');
    const nameInput = form.querySelector('[name="name"]');

    const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
    const fullName = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';
    const listId = form.dataset.klaviyoListId || '';
    const companyId = this.dataset.klaviyoCompanyId || '';
    const successMessage =
      this.dataset.notifySuccess || "You're on the list. We'll be in touch.";
    const errorMessage =
      this.dataset.notifyError || 'Something went wrong. Please try again.';
    const pendingMessage = this.dataset.notifyPending || 'Adding to list…';

    const setMessage = (text, type = '') => {
      if (!(message instanceof HTMLElement)) return;
      message.hidden = !text;
      message.textContent = text;
      message.classList.toggle('calendar-events__notify-message--error', type === 'error');
      message.classList.toggle('calendar-events__notify-message--success', type === 'success');
    };

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage('Please enter a valid email address.', 'error');
      emailInput?.focus();
      return;
    }
    if (!fullName) {
      setMessage('Please enter your full name.', 'error');
      nameInput?.focus();
      return;
    }
    if (!companyId || !listId) {
      setMessage('Signup is unavailable for this event right now.', 'error');
      return;
    }

    const [firstName, ...rest] = fullName.split(/\s+/);
    const lastName = rest.join(' ');

    if (submit instanceof HTMLButtonElement || submit instanceof HTMLInputElement) {
      submit.disabled = true;
    }
    setMessage(pendingMessage);

    try {
      const profileAttributes = {
        email,
        first_name: firstName || fullName,
        properties: { calendar_event: form.dataset.eventTitle || '' },
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      };
      if (lastName) profileAttributes.last_name = lastName;

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2025-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Calendar event notify me',
                profile: { data: { type: 'profile', attributes: profileAttributes } },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        }
      );

      if (!response.ok && response.status !== 202) {
        let detail = '';
        try {
          const errJson = await response.json();
          detail = errJson?.errors?.[0]?.detail || '';
        } catch {
          /* ignore */
        }
        throw new Error(detail || `Klaviyo error ${response.status}`);
      }

      form.reset();
      const controls = form.querySelector('[data-ce-notify-controls]');
      if (controls instanceof HTMLElement) controls.hidden = true;
      setMessage(successMessage, 'success');
    } catch (error) {
      const detail =
        error instanceof Error && error.message && !error.message.startsWith('Klaviyo error')
          ? error.message
          : errorMessage;
      setMessage(detail, 'error');
    } finally {
      if (submit instanceof HTMLButtonElement || submit instanceof HTMLInputElement) {
        submit.disabled = false;
      }
    }
  };
}

if (!customElements.get('calendar-events')) {
  customElements.define('calendar-events', CalendarEventsComponent);
}
